"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  DatePicker,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { cn } from "@/lib/utils";
import {
  createTaskAction,
  updateTaskStatusAction,
  updateTaskAction,
  deleteTaskAction,
} from "@/actions/work-actions";
import {
  Plus,
  Search,
  Calendar,
  User,
  CheckCircle2,
  AlertCircle,
  PlayCircle,
  HelpCircle,
  Edit2,
  Trash2,
  Folder,
  Network,
  ArrowUpDown,
  SlidersHorizontal,
  ChevronRight,
  Flame,
} from "lucide-react";

interface TaskItem {
  id: string;
  title: string;
  projectId: string;
  projectTitle: string;
  assigneeId: string | null;
  assigneeName: string | null;
  status: "todo" | "in_progress" | "done" | "blocked";
  priority: "low" | "medium" | "high" | "critical";
  dueDate: string | null;
  createdAt: string;
}

interface ProjectItem {
  id: string;
  title: string;
}

interface AgentItem {
  id: string;
  name: string;
}

interface TasksClientProps {
  initialTasks: TaskItem[];
  projects: ProjectItem[];
  agents: AgentItem[];
}

export function TasksClient({
  initialTasks,
  projects,
  agents,
}: TasksClientProps) {
  const router = useRouter();
  const [tasksList, setTasksList] = useState<TaskItem[]>(initialTasks);
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "done" | "active">(
    "all",
  );
  const [groupBy, setGroupBy] = useState<"status" | "priority">("status");

  // Drag and Drop States
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData("text/plain", id);
    setActiveDragId(id);
  };

  const handleDragEnd = () => {
    setActiveDragId(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    if (dragOverColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || activeDragId;
    setActiveDragId(null);
    setDragOverColumn(null);

    if (!id) return;

    const task = tasksList.find((t) => t.id === id);
    if (!task) return;

    if (groupBy === "status") {
      if (task.status !== targetColumnId) {
        await handleQuickStatusChange(
          id,
          targetColumnId as any,
          task.projectId,
        );
      }
    } else {
      if (task.priority !== targetColumnId) {
        try {
          const res = await updateTaskAction({
            ...task,
            priority: targetColumnId as any,
          });
          if (res.error) {
            alert(res.error);
          } else {
            router.refresh();
          }
        } catch (err: any) {
          alert("Errore durante l'aggiornamento della priorità.");
        }
      }
    }
  };

  // Modals state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Create Form State
  const [newTitle, setNewTitle] = useState("");
  const [newProjectId, setNewProjectId] = useState("");
  const [newAssigneeId, setNewAssigneeId] = useState("");
  const [newStatus, setNewStatus] = useState<TaskItem["status"]>("todo");
  const [newPriority, setNewPriority] =
    useState<TaskItem["priority"]>("medium");
  const [newDueDate, setNewDueDate] = useState("");

  // Edit Form State
  const [editTitle, setEditTitle] = useState("");
  const [editProjectId, setEditProjectId] = useState("");
  const [editAssigneeId, setEditAssigneeId] = useState("");
  const [editStatus, setEditStatus] = useState<TaskItem["status"]>("todo");
  const [editPriority, setEditPriority] =
    useState<TaskItem["priority"]>("medium");
  const [editDueDate, setEditDueDate] = useState("");

  // Sync state with server props
  useEffect(() => {
    setTasksList(initialTasks);
  }, [initialTasks]);

  // Set default project in create form if projects are available
  useEffect(() => {
    if (projects.length > 0 && !newProjectId) {
      setNewProjectId(projects[0].id);
    }
  }, [projects, newProjectId]);

  const handleOpenCreateModal = () => {
    setNewTitle("");
    if (projects.length > 0) setNewProjectId(projects[0].id);
    setNewAssigneeId("");
    setNewStatus("todo");
    setNewPriority("medium");
    setNewDueDate("");
    setError("");
    setIsCreateOpen(true);
  };

  const handleOpenEditModal = (task: TaskItem) => {
    setSelectedTask(task);
    setEditTitle(task.title);
    setEditProjectId(task.projectId);
    setEditAssigneeId(task.assigneeId || "");
    setEditStatus(task.status);
    setEditPriority(task.priority);
    setEditDueDate(task.dueDate ? task.dueDate.substring(0, 10) : "");
    setError("");
    setIsEditOpen(true);
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newTitle.trim()) {
      setError("Il titolo del task è obbligatorio.");
      return;
    }
    if (!newProjectId) {
      setError("Seleziona un progetto.");
      return;
    }

    setLoading(true);
    try {
      const res = await createTaskAction({
        projectId: newProjectId,
        title: newTitle,
        assigneeId: newAssigneeId || null,
        status: newStatus,
        priority: newPriority,
        dueDate: newDueDate || null,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsCreateOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante la creazione del task.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTask) return;
    setError("");

    if (!editTitle.trim()) {
      setError("Il titolo del task è obbligatorio.");
      return;
    }
    if (!editProjectId) {
      setError("Seleziona un progetto.");
      return;
    }

    setLoading(true);
    try {
      const res = await updateTaskAction({
        id: selectedTask.id,
        projectId: editProjectId,
        title: editTitle,
        assigneeId: editAssigneeId || null,
        status: editStatus,
        priority: editPriority,
        dueDate: editDueDate || null,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsEditOpen(false);
        setSelectedTask(null);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante l'aggiornamento del task.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (
    e: React.MouseEvent,
    id: string,
    projectId: string,
  ) => {
    e.stopPropagation();
    if (!confirm("Sei sicuro di voler eliminare questo task?")) return;
    try {
      const res = await deleteTaskAction(id, projectId);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'eliminazione.");
    }
  };

  const handleQuickStatusChange = async (
    id: string,
    status: TaskItem["status"],
    projectId: string,
  ) => {
    try {
      const res = await updateTaskStatusAction(id, status, projectId);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'aggiornamento dello stato.");
    }
  };

  const handleToggleStatusFilter = (status: "done" | "active") => {
    setPriorityFilter("all");
    if (statusFilter === status) {
      setStatusFilter("all");
    } else {
      setStatusFilter(status);
    }
  };

  const handleTogglePriorityFilter = (priority: string) => {
    setStatusFilter("all");
    if (priorityFilter === priority) {
      setPriorityFilter("all");
    } else {
      setPriorityFilter(priority);
    }
  };

  const getCardBgColor = (task: TaskItem) => {
    if (task.status === "done")
      return "bg-card border-border hover:border-primary-500/50";
    if (!task.dueDate)
      return "bg-card border-border hover:border-primary-500/50";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = new Date(task.dueDate);
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      if (diffDays <= -2) {
        // Clearly Overdue (scaduto da più di 2 giorni): Red tint
        return "bg-error-soft/15 border-error/30 hover:border-error/50";
      } else {
        // Just Passed (ieri): Orange warning tint
        return "bg-warning-soft/20 border-warning/30 hover:border-warning/50";
      }
    } else if (diffDays === 0) {
      // Due Today: Orange/Amber tint
      return "bg-warning-soft/20 border-warning/35 hover:border-warning/55";
    } else if (diffDays <= 3) {
      // Incumbent (da 1 a 3 giorni): Info blue tint
      return "bg-info-soft/10 border-info/30 hover:border-info/50";
    }

    return "bg-card border-border hover:border-primary-500/50";
  };

  // Filtering
  const filteredTasks = tasksList.filter((task) => {
    const matchesSearch = task.title
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesProject =
      projectFilter === "all" || task.projectId === projectFilter;
    const matchesAssignee =
      assigneeFilter === "all" || task.assigneeId === assigneeFilter;
    const matchesPriority =
      priorityFilter === "all" || task.priority === priorityFilter;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "done" && task.status === "done") ||
      (statusFilter === "active" && task.status !== "done");
    return (
      matchesSearch &&
      matchesProject &&
      matchesAssignee &&
      matchesPriority &&
      matchesStatus
    );
  });

  // Analytics Metrics
  const totalCount = tasksList.length;
  const doneCount = tasksList.filter((t) => t.status === "done").length;
  const openCount = totalCount - doneCount;
  const criticalCount = tasksList.filter(
    (t) => t.priority === "critical" && t.status !== "done",
  ).length;
  const progressPercent =
    totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  // Group columns definition
  const columns =
    groupBy === "status"
      ? [
          {
            id: "todo",
            label: "Da Fare",
            color: "bg-card-muted border-t-text-muted",
          },
          {
            id: "in_progress",
            label: "In Corso",
            color: "bg-primary-500/5 border-t-primary-500",
          },
          {
            id: "blocked",
            label: "Bloccato",
            color: "bg-error-soft/30 border-t-error",
          },
          {
            id: "done",
            label: "Completato",
            color: "bg-success-soft/30 border-t-success",
          },
        ]
      : [
          {
            id: "low",
            label: "Bassa Priorità",
            color: "bg-card-muted border-t-success",
          },
          {
            id: "medium",
            label: "Media Priorità",
            color: "bg-card-muted border-t-warning",
          },
          {
            id: "high",
            label: "Alta Priorità",
            color: "bg-card-muted border-t-error",
          },
          {
            id: "critical",
            label: "Critico",
            color: "bg-card-muted border-t-category-risk",
          },
        ];

  const getPriorityBadge = (p: TaskItem["priority"]) => {
    switch (p) {
      case "low":
        return <Badge variant="success">Bassa</Badge>;
      case "medium":
        return <Badge variant="warning">Media</Badge>;
      case "high":
        return <Badge variant="error">Alta</Badge>;
      case "critical":
        return (
          <Badge
            variant="error"
            className="animate-pulse bg-error text-text-on-primary"
          >
            <Flame className="h-3 w-3 mr-0.5 fill-text-on-primary" />
            Critica
          </Badge>
        );
    }
  };

  const getStatusIcon = (s: TaskItem["status"]) => {
    switch (s) {
      case "todo":
        return <HelpCircle className="h-4 w-4 text-text-muted" />;
      case "in_progress":
        return (
          <PlayCircle className="h-4 w-4 text-primary-500 animate-spin-slow" />
        );
      case "blocked":
        return <AlertCircle className="h-4 w-4 text-error" />;
      case "done":
        return <CheckCircle2 className="h-4 w-4 text-success" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Analytics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card
          onClick={() => handleToggleStatusFilter("done")}
          className={cn(
            "p-4 flex flex-col justify-between cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all select-none",
            statusFilter === "done"
              ? "ring-1 ring-primary-600/50 bg-primary-500/[0.02] border-primary-500"
              : "border-border bg-card",
          )}
        >
          <div className="space-y-1">
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Avanzamento Globale
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{progressPercent}%</span>
              <span className="text-xs text-text-secondary">
                ({doneCount}/{totalCount} completati)
              </span>
            </div>
          </div>
          <div className="w-full bg-border rounded-full h-1.5 mt-3 overflow-hidden">
            <div
              className="bg-primary-600 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </Card>

        <Card
          onClick={() => handleToggleStatusFilter("active")}
          className={cn(
            "p-4 flex flex-col justify-between cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all select-none",
            statusFilter === "active"
              ? "ring-1 ring-primary-600/50 bg-primary-500/[0.02] border-primary-500"
              : "border-border bg-card",
          )}
        >
          <div>
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Task Aperti
            </span>
            <p className="text-2xl font-bold mt-1 text-primary-600">
              {openCount}
            </p>
            <p className="text-xs text-text-secondary mt-1">
              Lavorazioni attive in corso
            </p>
          </div>
        </Card>

        <Card
          onClick={() => handleTogglePriorityFilter("critical")}
          className={cn(
            "p-4 flex flex-col justify-between border-l-4 border-l-error cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all select-none",
            priorityFilter === "critical"
              ? "ring-1 ring-error/50 bg-error/[0.02] border-error"
              : "border-border bg-card",
          )}
        >
          <div>
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Bloccanti / Critici
            </span>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-bold text-error">{criticalCount}</p>
              {criticalCount > 0 && (
                <Flame className="h-5 w-5 text-error animate-bounce" />
              )}
            </div>
            <p className="text-xs text-text-secondary mt-1">
              Richiedono attenzione immediata
            </p>
          </div>
        </Card>

        <Card className="p-4 flex flex-col justify-between bg-card-muted/10 border-dashed h-full">
          <div className="space-y-1">
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Azioni Rapide
            </span>
            <p className="text-[11px] text-text-secondary leading-normal">
              Crea e pianifica nuove attività nel flusso operativo.
            </p>
          </div>
          <Button
            onClick={handleOpenCreateModal}
            className="w-full gap-1.5 mt-3 h-8 text-xs cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
            Nuovo Task
          </Button>
        </Card>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col gap-4 bg-card p-4 rounded-xl border border-border">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative w-full lg:max-w-xs">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
            <Input
              placeholder="Cerca task..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full lg:w-auto">
            <Select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="text-xs"
            >
              <option value="all">Tutti i progetti</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </Select>

            <Select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="text-xs"
            >
              <option value="all">Tutti i membri</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>

            <Select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="text-xs"
            >
              <option value="all">Tutte le priorità</option>
              <option value="low">Bassa</option>
              <option value="medium">Media</option>
              <option value="high">Alta</option>
              <option value="critical">Critica</option>
            </Select>

            <div className="flex border border-border rounded-lg overflow-hidden shrink-0">
              <button
                onClick={() => setGroupBy("status")}
                className={`flex-1 py-1 px-3 text-xs font-semibold transition-colors ${
                  groupBy === "status"
                    ? "bg-primary-600 text-text-on-primary"
                    : "bg-card hover:bg-card-muted text-text-secondary"
                }`}
              >
                Stato
              </button>
              <button
                onClick={() => setGroupBy("priority")}
                className={`flex-1 py-1 px-3 text-xs font-semibold transition-colors ${
                  groupBy === "priority"
                    ? "bg-primary-600 text-text-on-primary"
                    : "bg-card hover:bg-card-muted text-text-secondary"
                }`}
              >
                Priorità
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {columns.map((col) => {
          const colTasks = filteredTasks.filter((t) =>
            groupBy === "status" ? t.status === col.id : t.priority === col.id,
          );

          return (
            <div
              key={col.id}
              onDragOver={(e) => handleDragOver(e, col.id)}
              onDragLeave={() => setDragOverColumn(null)}
              onDrop={(e) => handleDrop(e, col.id)}
              className={`rounded-xl border transition-all p-3 flex flex-col min-h-[450px] ${
                dragOverColumn === col.id
                  ? "border-primary-500/80 bg-primary-50/5 ring-2 ring-primary-500/10"
                  : "border-border/85 bg-card/40"
              }`}
            >
              {/* Column Header */}
              <div className="flex justify-between items-center mb-3 pb-2 border-b border-border/40">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-text-primary uppercase tracking-wider">
                    {col.label}
                  </span>
                  <Badge variant="neutral" className="px-1.5 py-0">
                    {colTasks.length}
                  </Badge>
                </div>
              </div>

              {/* Column Body */}
              <div className="flex-1 space-y-3 overflow-y-auto scrollbar-thin max-h-[600px] pr-0.5">
                {colTasks.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center border border-dashed border-border/40 rounded-lg p-6 text-center text-text-muted bg-card-muted/10">
                    <p className="text-xs">Nessun task</p>
                  </div>
                ) : (
                  colTasks.map((task) => (
                    <Card
                      key={task.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleOpenEditModal(task)}
                      className={cn(
                        "p-3 border-t-4 hover:shadow-md transition-all space-y-2 relative group cursor-pointer select-none hover:scale-[1.01] active:scale-[0.99]",
                        getCardBgColor(task),
                        activeDragId === task.id &&
                          "opacity-45 scale-[0.98] border-dashed border-primary-500/40",
                      )}
                      style={{
                        borderTopColor:
                          groupBy === "status"
                            ? undefined
                            : `var(--color-priority-${task.priority})`,
                      }}
                    >
                      <div className="flex justify-between items-center gap-2">
                        <Link
                          href={`/work/projects/${task.projectId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-text-muted hover:text-primary-600 hover:underline min-w-0 flex-1 font-medium flex items-center gap-1 transition-colors"
                          title={task.projectTitle}
                        >
                          <Folder className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{task.projectTitle}</span>
                        </Link>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {/* Graph Link (modify button) */}
                          <Link
                            href={`/explore?nodeId=${task.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                            title="Vedi nel Grafo"
                          >
                            <Network className="h-3 w-3" />
                          </Link>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(task);
                            }}
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                            title="Modifica Task"
                          >
                            <Edit2 className="h-3 w-3" />
                          </button>
                          <button
                            onClick={(e) =>
                              handleDeleteTask(e, task.id, task.projectId)
                            }
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-error transition-colors"
                            title="Elimina Task"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>

                      <h4 className="text-sm font-semibold text-text-primary leading-snug line-clamp-2">
                        <Link
                          href={`/work/projects/${task.projectId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-primary-600 hover:underline transition-colors"
                        >
                          {task.title}
                        </Link>
                      </h4>

                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        {groupBy === "status" ? (
                          getPriorityBadge(task.priority)
                        ) : (
                          <div className="flex items-center gap-1 text-xs font-semibold">
                            {getStatusIcon(task.status)}
                            <span className="capitalize text-text-secondary text-[11px]">
                              {task.status.replace("_", " ")}
                            </span>
                          </div>
                        )}

                        {task.dueDate && (
                          <span
                            className={`text-[11px] font-medium flex items-center gap-1 ml-auto shrink-0 ${
                              new Date(task.dueDate) < new Date() &&
                              task.status !== "done"
                                ? "text-error"
                                : "text-text-muted"
                            }`}
                          >
                            <Calendar className="h-3 w-3" />
                            {new Date(task.dueDate).toLocaleDateString(
                              "it-IT",
                              {
                                day: "2-digit",
                                month: "2-digit",
                              },
                            )}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center border-t border-border/40 pt-2 mt-2">
                        {task.assigneeId ? (
                          <Link
                            href={`/people/${task.assigneeId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-primary-600 hover:underline max-w-[180px] truncate transition-colors"
                          >
                            <User className="h-3 w-3 shrink-0" />
                            <span className="truncate">
                              {task.assigneeName}
                            </span>
                          </Link>
                        ) : (
                          <div className="flex items-center gap-1.5 text-xs text-text-muted max-w-[180px] truncate">
                            <User className="h-3 w-3 shrink-0" />
                            <span className="truncate">Non assegnato</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Task</DialogTitle>
            <DialogDescription>
              Aggiungi un'attività operativa associata ad un progetto.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateTask} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="task-title">Titolo dell'Attività</Label>
              <Input
                id="task-title"
                placeholder="es. Progettare wireframe dashboard"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="task-project">Progetto Collegato</Label>
                <Select
                  id="task-project"
                  value={newProjectId}
                  onChange={(e) => setNewProjectId(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="">Seleziona progetto...</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="task-assignee">Assegnatario (Assignee)</Label>
                <Select
                  id="task-assignee"
                  value={newAssigneeId}
                  onChange={(e) => setNewAssigneeId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Non assegnato (Nessuno)</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="task-status">Stato Iniziale</Label>
                <Select
                  id="task-status"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="todo">Da Fare</option>
                  <option value="in_progress">In Corso</option>
                  <option value="blocked">Bloccato</option>
                  <option value="done">Completato</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="task-priority">Priorità</Label>
                <Select
                  id="task-priority"
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="low">Bassa</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="critical">Critica</option>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="task-duedate">Data Scadenza (Opzionale)</Label>
              <DatePicker
                id="task-duedate"
                value={newDueDate}
                onChange={(val) => setNewDueDate(val)}
                disabled={loading}
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsCreateOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                Crea Task
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Task</DialogTitle>
            <DialogDescription>
              Modifica i dettagli del task selezionato.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdateTask} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-task-title">Titolo dell'Attività</Label>
              <Input
                id="edit-task-title"
                placeholder="es. Progettare wireframe dashboard"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-task-project">Progetto Collegato</Label>
                <Select
                  id="edit-task-project"
                  value={editProjectId}
                  onChange={(e) => setEditProjectId(e.target.value)}
                  disabled={loading}
                  required
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-task-assignee">
                  Assegnatario (Assignee)
                </Label>
                <Select
                  id="edit-task-assignee"
                  value={editAssigneeId}
                  onChange={(e) => setEditAssigneeId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Non assegnato (Nessuno)</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-task-status">Stato</Label>
                <Select
                  id="edit-task-status"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="todo">Da Fare</option>
                  <option value="in_progress">In Corso</option>
                  <option value="blocked">Bloccato</option>
                  <option value="done">Completato</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-task-priority">Priorità</Label>
                <Select
                  id="edit-task-priority"
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="low">Bassa</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="critical">Critica</option>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-task-duedate">
                Data Scadenza (Opzionale)
              </Label>
              <DatePicker
                id="edit-task-duedate"
                value={editDueDate}
                onChange={(val) => setEditDueDate(val)}
                disabled={loading}
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsEditOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
